<svelte:options />

<script>
    import { run } from 'svelte/legacy';
    import { onMount, tick } from 'svelte';
    import { createEventDispatcher } from 'svelte';
    /**
     * @typedef {Object} Props
     * @property {any} [name]
     * @property {any} value
     * @property {string} [label]
     * @property {any} [options]
     * @property {boolean} [required] - eslint-disable-line no-unused-vars
     * @property {any} [associatedID]
     * @property {boolean} [disabled]
     * @property {string} [color]
     * @property {boolean} [bold]
     * @property {string} [backgroundColor]
     * @property {any} [ariaLabel]
     * @property {boolean} [searchable]
     * @property {string} [searchPlaceholder]
     * @property {string} [searchAriaLabel]
     */
    let {
        name = null,
        value = $bindable(),
        label = $bindable('Please Select'),
        options = [],
        required = false,
        associatedID = null,
        disabled = false,
        color = '#ddd',
        bold = false,
        backgroundColor = 'rgba(255,255,255,0.2)',
        ariaLabel = null,
        searchable = false,
        searchPlaceholder = 'Search options...',
        searchAriaLabel = ariaLabel ? `Search ${ariaLabel}` : 'Search options',
        style: inlineStyle = '',
        onchange = () => {},
    } = $props();

    const dispatch = createEventDispatcher();
    let me = $state(null);
    let container = $state(null);
    let searchInput = $state(null);
    let isSearchMenuOpen = $state(false);
    let searchTerm = $state('');

    let filteredOptions = $derived.by(() => {
        if (!searchable) {
            return options;
        }

        const normalized = searchTerm.trim().toLowerCase();
        if (!normalized) {
            return options;
        }

        return options.filter((option) =>
            (option.displayName || '').toLowerCase().includes(normalized)
        );
    });

    function syncLabelToValue() {
        const matched = options.find((o) => o.name === value);
        if (matched && label !== matched.displayName) {
            label = matched.displayName;
        }
    }

    function handleMenuClick(e) {
        value = e.target.value;
        label = options.find((o) => o.name === value)?.displayName;

        onchange({
            detail: {
                originalEvent: e,
                name: label,
                value,
                associatedID,
            },
        });
    }

    function handleOptionSelect(option) {
        value = option.name;
        label = option.displayName;

        onchange({
            detail: {
                originalEvent: {
                    target: {
                        value,
                    },
                },
                name: label,
                value,
                associatedID,
            },
        });

        closeSearchMenu();
    }

    async function toggleSearchMenu(e) {
        e.preventDefault();
        e.stopPropagation();

        if (disabled) return;

        isSearchMenuOpen = !isSearchMenuOpen;

        if (isSearchMenuOpen) {
            await tick();
            searchInput?.focus();
        } else {
            searchTerm = '';
        }
    }

    function closeSearchMenu() {
        if (!isSearchMenuOpen) return;
        isSearchMenuOpen = false;
        searchTerm = '';
    }

    onMount(() => {
        function handleClickOutside(e) {
            if (!isSearchMenuOpen) return;
            if (!container?.contains(e.target)) {
                closeSearchMenu();
            }
        }

        window.addEventListener('pointerdown', handleClickOutside, true);

        return () => {
            window.removeEventListener('pointerdown', handleClickOutside, true);
        };
    });

    run(() => {
        options;  
        value;  
        syncLabelToValue();
    });

    export {
        name,
        value,
        label,
        options,
        required,
        associatedID,
        disabled,
        color,
        bold,
        backgroundColor,
        ariaLabel,
        searchable,
        searchPlaceholder,
        searchAriaLabel,
        inlineStyle,
    };
</script>

<div
    bind:this={me}
    class="input"
    {value}
    style="--color:{color};--background-color:{backgroundColor};"
>
    <div 
        class="custom {searchable ? 'searchable' : ''}" 
        bind:this={container}
        style={inlineStyle}
    >
        {#if searchable}
            <button
                class="searchable-trigger interactive {bold ? 'bold' : ''}"
                onclick={toggleSearchMenu}
                type="button"
                aria-haspopup="listbox"
                aria-expanded={isSearchMenuOpen}
                aria-label={ariaLabel || label || 'Select option'}
                {disabled}
            >
                <span>{label}</span>
                <i class="material-icons" aria-hidden="true">expand_more</i>
            </button>
            {#if isSearchMenuOpen}
                <div
                    class="searchable-panel"
                    role="listbox"
                    aria-label={ariaLabel || 'Options'}
                >
                    <div class="search-wrapper">
                        <input
                            type="text"
                            class="search-input"
                            placeholder={searchPlaceholder}
                            bind:this={searchInput}
                            bind:value={searchTerm}
                            aria-label={searchAriaLabel}
                            {disabled}
                        />
                    </div>
                    {#if filteredOptions.length === 0}
                        <div class="no-results">No matching options</div>
                    {:else}
                        <ul class="searchable-options">
                            {#each filteredOptions as option}
                                <li>
                                    <button
                                        type="button"
                                        class="option-row {option.name === value ? 'active' : ''}"
                                        role="option"
                                        aria-selected={option.name === value}
                                        onclick={() => handleOptionSelect(option)}
                                    >
                                        {option.displayName}
                                    </button>
                                </li>
                            {/each}
                        </ul>
                    {/if}
                </div>
            {/if}
        {:else}
            {#if ariaLabel}
                <select
                    id={name}
                    {name}
                    onchange={handleMenuClick}
                    class="interactive"
                    aria-label={ariaLabel}
                    disabled={disabled}
                >
                    {#each filteredOptions as o}
                        <option value={o.name} selected={o.name === value}
                            >{o.displayName}</option
                        >
                    {/each}
                </select>
            {:else}
                <select
                    id={name}
                    {name}
                    onchange={handleMenuClick}
                    class="interactive"
                    disabled={disabled}
                >
                    {#each filteredOptions as o}
                        <option value={o.name} selected={o.name === value}
                            >{o.displayName}</option
                        >
                    {/each}
                </select>
            {/if}
        {/if}
    </div>
</div>

<style>
    .input {
        display: flex;
    }

    .custom {
        position: relative;
        height: 40px;
    }

    .custom.searchable {
        height: auto;
    }

    .custom select {
        appearance: none;
        -webkit-appearance: none;
        width: 100%;
        padding: 0.5rem 2rem 0.5rem 0.75rem;
        border-radius: 0.25rem;
        color: var(--color);
        background-color: var(--background-color);
        height: 100%;
        font-size: 0.9rem;
    }

    .custom:not(.searchable)::after {
        content: 'expand_more';
        font-family: 'Material Icons';
        position: absolute;
        right: 10px;
        top: 50%;
        transform: translateY(-50%);
        pointer-events: none;
        color: var(--color);
        font-size: 24px;
    }

    .custom select option {
        color: #000;
    }

    .search-wrapper {
        margin-bottom: 0.35rem;
    }

    .search-input {
        width: 100%;
        padding: 0.35rem 0.5rem;
        border-radius: 0.25rem;
        border: 1px solid rgba(255, 255, 255, 0.3);
        background-color: rgba(0, 0, 0, 0.2);
        color: var(--color);
    }

    .search-input:focus {
        outline: 2px solid var(--color);
    }

    .search-input:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }

    .no-results {
        font-size: 1rem;
        color: var(--color);
        opacity: 0.75;
        margin-top: 0.35rem;
        margin-bottom: 0.35rem;
    }

    .searchable-trigger {
        width: 100%;
        padding: 0.5rem 0.75rem;
        border-radius: 0.25rem;
        border: none;
        display: flex;
        justify-content: space-between;
        align-items: center;
        background-color: var(--background-color);
        color: var(--color);
        font-size: 0.9rem;
        cursor: pointer;
    }

    .searchable-trigger.bold {
        font-weight: 600;
    }

    .searchable-trigger:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }

    .searchable-panel {
        position: absolute;
        top: calc(100% + 0.25rem);
        left: 0;
        width: 100%;
        z-index: 10;
        background-color: rgba(24, 24, 24, 0.95);
        border-radius: 0.35rem;
        box-shadow: 0 6px 20px rgba(0, 0, 0, 0.4);
        padding: 0.5rem;
        backdrop-filter: blur(6px);
        /* The panel is always dark, so don't inherit the trigger's colour
           (e.g. black on the yellow Tabs dropdown). */
        --color: #ddd;
        color: var(--color);
    }

    .searchable-options {
        list-style: none;
        margin: 0;
        padding: 0;
        max-height: 200px;
        overflow-y: auto;
    }

    .searchable-options li + li {
        margin-top: 0.25rem;
    }

    .option-row {
        width: 100%;
        text-align: left;
        border: none;
        background: rgba(255, 255, 255, 0.05);
        color: var(--color);
        border-radius: 0.25rem;
        padding: 0.4rem 0.5rem;
        cursor: pointer;
    }

    .option-row:hover,
    .option-row.active {
        background: rgba(255, 255, 255, 0.15);
    }
</style>
